import { useEffect, useRef } from 'react'
import Collapse from 'react-bootstrap/Collapse'
import Form from 'react-bootstrap/Form'
import { useSignal } from '@preact/signals-react'

import { EMV_AIDS_GROUP_ID, findEmvAidSettingsPath, groupEmvAidPathsByBrand, isDeviceKeyField, isInternalDeviceField } from '../configTemplateSchemaUtils'

function TriStateCheckbox({ checked, indeterminate, onChange, className }) {
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate
    }, [indeterminate])
    return (
        <Form.Check
            ref={ref}
            type="checkbox"
            label={null}
            checked={checked}
            onChange={onChange}
            className={className}
        />
    )
}

function GroupCheckbox({ checked, indeterminate, label, onChange, className }) {
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate
    }, [indeterminate])
    return (
        <Form.Check
            ref={ref}
            type="checkbox"
            label={label}
            checked={checked}
            onChange={onChange}
            className={className}
        />
    )
}

function CollapseArrow({ isCollapsed, onClick }) {
    return (
        <span
            onClick={e => { e.preventDefault(); e.stopPropagation(); onClick() }}
            style={{
                display: 'inline-block',
                width: '1rem',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                verticalAlign: 'middle',
                marginRight: '2px',
                color: '#000',
            }}
        >
            {isCollapsed ? '▶' : '▼'}
        </span>
    )
}

// Device schemas nest fields inside untitled object wrappers (connection,
// manualCardEntry, emv, privateLabel — and emv.emvAidsReactUi a level deeper).
// Recursively flatten those so the leaf fields — which carry the title and
// s4-group-id — are the ones shown in the checklist. Unlike the main config
// schema, device fields use `title` rather than `s4-caption`.
function collectDeviceFields(deviceTypeName, configProps) {
    const fields = []
    const walk = (props, prefix, isTopLevel) => {
        for (const [fieldKey, field] of Object.entries(props || {})) {
            if (field['s4-display'] === false) continue
            if (isInternalDeviceField(field)) continue
            // deviceType and terminalList are the server-side merge key; always saved, never optional
            if (isDeviceKeyField(fieldKey, isTopLevel)) continue
            const dotPath = `${prefix}.${fieldKey}`
            if (field.type === 'object' && field.properties) {
                walk(field.properties, dotPath, false)
            } else {
                fields.push({
                    dotPath,
                    caption: field.title || field['s4-caption'] || fieldKey,
                    groupId: field['s4-group-id'] || '__ungrouped__',
                })
            }
        }
    }
    walk(configProps, `device.${deviceTypeName}`, true)
    return fields
}

export default function ConfigTemplateDeviceChecklist({ deviceSchemas, checkedFieldsSignal }) {

    // Start every section expanded — this list is scoped to the single device type
    // being edited, so collapsing it would hide the whole thing. Sections remain
    // collapsible by hand via the arrow.
    const collapsedSignal = useSignal({})

    if (!deviceSchemas?.length) return null

    const checkedPaths = checkedFieldsSignal.value

    const toggleSection = (key) => {
        collapsedSignal.value = { ...collapsedSignal.value, [key]: !collapsedSignal.value[key] }
    }

    // Checking any EMV AID field pulls in that device's aidSettings, which is the only
    // part of the group the UTG actually reads. Unchecking the last one drops it again.
    const applyEmvAidRule = (next, emvAids) => {
        if (!emvAids?.settingsPath) return next
        const anyInputChecked = emvAids.inputPaths.some(p => next.has(p))
        if (anyInputChecked) next.add(emvAids.settingsPath)
        else next.delete(emvAids.settingsPath)
        return next
    }

    // A card brand's AID limits only mean anything alongside its checkbox, so toggling
    // any one field in a brand cluster toggles the whole cluster.
    const expandEmvAidBrand = (dotPath, emvAids) => {
        const cluster = emvAids?.brandByPath?.get(dotPath)
        return cluster ?? [dotPath]
    }

    const handleFieldToggle = (dotPath, isChecked, emvAids) => {
        const next = new Set(checkedPaths)
        for (const p of expandEmvAidBrand(dotPath, emvAids)) {
            if (isChecked) next.add(p)
            else next.delete(p)
        }
        checkedFieldsSignal.value = applyEmvAidRule(next, emvAids)
    }

    const handleGroupToggle = (paths, isChecked, emvAids) => {
        const next = new Set(checkedPaths)
        for (const p of paths) {
            if (isChecked) next.add(p)
            else next.delete(p)
        }
        checkedFieldsSignal.value = applyEmvAidRule(next, emvAids)
    }

    return (
        <div className="config-template-checklist" style={{ fontSize: '0.85rem' }}>
            {deviceSchemas.map(({ name: deviceTypeName, schemaValue }) => {
                const configProps = schemaValue?.properties?.config?.properties
                if (!configProps) return null

                const displayName = configProps.deviceType?.default || deviceTypeName
                const s4Groups = schemaValue.properties.config['s4-groups'] || []
                const groupMap = {}
                for (const g of s4Groups) {
                    groupMap[g.id] = { id: g.id, label: g.displayName, paths: [], fields: [] }
                }
                groupMap['__ungrouped__'] = { id: '__ungrouped__', label: null, paths: [], fields: [] }

                const deviceFields = collectDeviceFields(deviceTypeName, configProps)
                for (const { dotPath, caption, groupId } of deviceFields) {
                    const bucket = groupMap[groupId] || groupMap['__ungrouped__']
                    bucket.paths.push(dotPath)
                    bucket.fields.push({ dotPath, caption })
                }

                // aidSettings plus the AID inputs that feed it, for this device type.
                // brandByPath maps each AID input to every path in its card-brand cluster
                // so a single toggle can select or clear the whole brand at once.
                const settingsPath = findEmvAidSettingsPath(deviceTypeName, configProps)
                let emvAids = null
                if (settingsPath) {
                    const inputPaths = deviceFields
                        .filter(f => f.groupId === EMV_AIDS_GROUP_ID && f.dotPath !== settingsPath)
                        .map(f => f.dotPath)
                    const brandByPath = new Map()
                    for (const cluster of groupEmvAidPathsByBrand(inputPaths).values()) {
                        for (const p of cluster) brandByPath.set(p, cluster)
                    }
                    emvAids = { settingsPath, inputPaths, brandByPath }
                }

                const groups = [
                    ...s4Groups.map(g => groupMap[g.id]).filter(g => g.fields.length > 0),
                    ...(groupMap['__ungrouped__'].fields.length > 0 ? [groupMap['__ungrouped__']] : [])
                ]
                const sectionPaths = groups.flatMap(g => g.paths)
                if (sectionPaths.length === 0) return null

                const checkedCount = sectionPaths.filter(p => checkedPaths.has(p)).length
                const allChecked = checkedCount === sectionPaths.length
                const indeterminate = checkedCount > 0 && !allChecked
                const isCollapsed = !!collapsedSignal.value[deviceTypeName]

                return (
                    <div key={deviceTypeName} className="mb-3">
                        <div className="d-flex align-items-center">
                            <TriStateCheckbox
                                checked={allChecked}
                                indeterminate={indeterminate}
                                onChange={e => handleGroupToggle(sectionPaths, e.target.checked, emvAids)}
                                className="mb-0 me-1"
                            />
                            <CollapseArrow isCollapsed={isCollapsed} onClick={() => toggleSection(deviceTypeName)} />
                            <strong style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSection(deviceTypeName)}>
                                {displayName}
                            </strong>
                        </div>
                        <Collapse in={!isCollapsed}>
                            <div style={{ paddingLeft: '1.5rem' }}>
                                {groups.map(group => {
                                    const groupCheckedCount = group.paths.filter(p => checkedPaths.has(p)).length
                                    const groupAllChecked = groupCheckedCount === group.paths.length
                                    const groupIndeterminate = groupCheckedCount > 0 && !groupAllChecked
                                    return (
                                        <div key={group.id} className="mb-2">
                                            {group.label && (
                                                <GroupCheckbox
                                                    checked={groupAllChecked}
                                                    indeterminate={groupIndeterminate}
                                                    label={<em>{group.label}</em>}
                                                    onChange={e => handleGroupToggle(group.paths, e.target.checked, emvAids)}
                                                    className="mb-1"
                                                />
                                            )}
                                            <div style={{ paddingLeft: group.label ? '1rem' : 0 }}>
                                                {group.fields.map(({ dotPath, caption }) => (
                                                    <Form.Check
                                                        key={dotPath}
                                                        type="checkbox"
                                                        label={caption}
                                                        checked={checkedPaths.has(dotPath)}
                                                        onChange={e => handleFieldToggle(dotPath, e.target.checked, emvAids)}
                                                        className="mb-0"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </Collapse>
                    </div>
                )
            })}
        </div>
    )
}
